/**
 * PricingSection.ts
 *
 * Secao de precos da landing page do SwimSentry.
 * Modelo freemium: Free Trial vs Pro.
 *
 * Principios de conversao:
 * - Ancoragem de preco (mostra mensal ao lado do anual)
 * - Social proof no badge "Most Popular"
 * - Urgencia com trial limitado
 */

export interface PricingPlan {
  id: string;
  name: string;
  price: string;
  period?: string;
  description?: string;
  features: Array<{
    text: string;
    included: boolean;
  }>;
  ctaText: string;
  ctaHref: string;
  featured?: boolean;
  badge?: string;
}

export interface PricingConfig {
  title: string;
  subtitle?: string;
  plans: PricingPlan[];
  shareMessage?: string;
}

const DEFAULT_CONFIG: PricingConfig = {
  title: 'Simple, Transparent Pricing',
  subtitle: 'Try free, upgrade when you\'re ready',
  plans: [
    {
      id: 'free',
      name: 'FREE TRIAL',
      price: '$0',
      description: 'Test the full system',
      features: [
        { text: '2 minutes per session', included: true },
        { text: 'Full AI detection', included: true },
        { text: 'Loud alarm (85dB)', included: true },
        { text: 'SMS alerts', included: false },
        { text: 'Unlimited sessions', included: false }
      ],
      ctaText: 'Try Free',
      ctaHref: '/app'
    },
    {
      id: 'pro',
      name: 'SWIMSENTRY PRO',
      price: '$149',
      period: '/year ($12.42/month)',
      features: [
        { text: 'Unlimited monitoring', included: true },
        { text: 'Full AI detection', included: true },
        { text: 'SMS alerts to your phone', included: true },
        { text: 'Multi-device support', included: true },
        { text: 'Priority support', included: true },
        { text: '"Founder" badge', included: true }
      ],
      ctaText: 'Get Pro Access',
      ctaHref: '#checkout',
      featured: true,
      badge: 'Most Popular'
    }
  ],
  shareMessage: 'Share SwimSentry with a friend and get 7 extra days free'
};

export class PricingSection {
  private container: HTMLElement;
  private config: PricingConfig;

  constructor(container: HTMLElement, config: Partial<PricingConfig> = {}) {
    this.container = container;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  render(): void {
    this.container.innerHTML = `
      <section class="pricing-section landing-section" id="pricing">
        <h2 class="section-title">${this.config.title}</h2>
        ${this.config.subtitle ? `<p class="section-subtitle text-center">${this.config.subtitle}</p>` : ''}

        <div class="pricing-cards">
          ${this.config.plans.map(plan => this.renderPlanCard(plan)).join('')}
        </div>

        ${this.config.shareMessage ? `
          <p class="pricing-share text-center" style="margin-top: 32px; color: var(--ss-text-secondary); font-size: 0.9rem;">
            &#128161; ${this.config.shareMessage}
          </p>
        ` : ''}
      </section>
    `;

    this.attachEventListeners();
  }

  private renderPlanCard(plan: PricingPlan): string {
    const featuredClass = plan.featured ? 'featured' : '';

    return `
      <div class="pricing-card ${featuredClass}" data-plan="${plan.id}">
        ${plan.badge ? `<span class="pricing-badge">${plan.badge}</span>` : ''}

        <p class="pricing-name">${plan.name}</p>
        <p class="pricing-price">${plan.price}</p>
        ${plan.period ? `<p class="pricing-period">${plan.period}</p>` : '<p class="pricing-period">forever free</p>'}

        <ul class="pricing-features">
          ${plan.features.map(feature => this.renderFeature(feature)).join('')}
        </ul>

        <a href="${plan.ctaHref}" class="btn-primary pricing-cta" data-plan-id="${plan.id}">
          ${plan.ctaText}
        </a>
      </div>
    `;
  }

  private renderFeature(feature: { text: string; included: boolean }): string {
    const iconClass = feature.included ? 'yes' : 'no';
    const icon = feature.included ? '&#10003;' : '&#10007;';
    const disabledClass = feature.included ? '' : 'disabled';

    return `
      <li class="pricing-feature ${disabledClass}">
        <span class="pricing-feature-icon ${iconClass}">${icon}</span>
        <span>${feature.text}</span>
      </li>
    `;
  }

  private attachEventListeners(): void {
    // Track CTA clicks
    const ctas = this.container.querySelectorAll('.pricing-cta');
    ctas.forEach(cta => {
      cta.addEventListener('click', (_e) => {
        const planId = (cta as HTMLElement).dataset.planId;
        this.onPlanSelect(planId || 'unknown');
      });
    });
  }

  /**
   * Callback quando usuario seleciona um plano
   */
  private onPlanSelect(planId: string): void {
    // Analytics event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'select_plan', {
        plan_id: planId
      });
    }

    // Dispatch custom event para integracao
    this.container.dispatchEvent(new CustomEvent('plan-selected', {
      detail: { planId },
      bubbles: true
    }));
  }

  /**
   * Atualiza preco de um plano (util para A/B testing)
   */
  updatePrice(planId: string, price: string, period?: string): void {
    const card = this.container.querySelector(`[data-plan="${planId}"]`);
    if (card) {
      const priceEl = card.querySelector('.pricing-price');
      const periodEl = card.querySelector('.pricing-period');
      if (priceEl) priceEl.textContent = price;
      if (periodEl && period) periodEl.textContent = period;
    }
  }

  /**
   * Destaca um plano especifico (promocao)
   */
  highlightPlan(planId: string): void {
    const cards = this.container.querySelectorAll('.pricing-card');
    cards.forEach(card => {
      card.classList.remove('featured');
      const badge = card.querySelector('.pricing-badge');
      if (badge) badge.remove();
    });

    const targetCard = this.container.querySelector(`[data-plan="${planId}"]`);
    if (targetCard) {
      targetCard.classList.add('featured');
      targetCard.insertAdjacentHTML('afterbegin', '<span class="pricing-badge">Best Value</span>');
    }
  }
}

export default PricingSection;
